import { Menu, ChevronLeft, LayoutDashboard, LogIn, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const Logo = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center select-none" data-testid="logo-btn">
    <div className="flex items-center gap-0">
      <span className="bg-[#ff4e15] text-black font-black px-2 py-1 text-sm heading-font tracking-wider">MY</span>
      <span className="border-2 border-[#ff4e15] text-[#ff4e15] font-black px-2 py-1 text-sm heading-font tracking-wider">HEALTHYFY</span>
    </div>
  </button>
);

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div
      className="hidden sm:inline-flex items-center text-sm border border-[#2a2b2f] rounded-sm overflow-hidden"
      data-testid="lang-switch"
    >
      <button
        onClick={() => setLang('en')}
        className={`px-2 py-1 transition-colors ${
          lang === 'en' ? 'bg-[#ff4e15] text-white font-bold' : 'text-white/70 hover:text-[#ff4e15]'
        }`}
        aria-label="English"
        data-testid="lang-en-btn"
      >
        EN
      </button>
      <button
        onClick={() => setLang('hi')}
        className={`px-2 py-1 transition-colors ${
          lang === 'hi' ? 'bg-[#ff4e15] text-white font-bold' : 'text-white/70 hover:text-[#ff4e15]'
        }`}
        aria-label="हिंदी"
        data-testid="lang-hi-btn"
      >
        हिं
      </button>
    </div>
  );
};

const Header = ({ showBack = false, onBack, progress = null, title = null }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="w-full sticky top-0 z-30 bg-[#0d0e10]">
      <div className="flex items-center justify-between px-4 md:px-8 py-4">
        <div className="flex items-center gap-3">
          <Logo onClick={() => navigate('/')} />
        </div>

        {title && (
          <h2 className="hidden md:block text-lg md:text-xl font-bold text-white text-center absolute left-1/2 -translate-x-1/2">
            {title}
          </h2>
        )}

        <div className="flex items-center gap-4 text-sm text-white">
          <a href="#help" className="hover:text-[#ff4e15] transition-colors hidden sm:inline">{t('help')}</a>
          <LangSwitch />

          {user ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="hidden sm:flex items-center gap-2 hover:text-[#ff4e15] transition-colors"
              data-testid="header-user-btn"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[#ff4e15]" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-[#ff4e15] flex items-center justify-center text-xs font-black">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </span>
              )}
              <span className="max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:flex items-center gap-1.5 hover:text-[#ff4e15] transition-colors"
              data-testid="header-signin-btn"
            >
              <LogIn size={14} /> {t('signIn')}
            </button>
          )}

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1 hover:text-[#ff4e15] transition-colors"
            aria-label={t('menu')}
            data-testid="menu-toggle-btn"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {title && (
        <div className="md:hidden text-center pb-3 text-white font-bold text-base">{title}</div>
      )}

      {progress !== null && (
        <div className="flex items-center gap-3 px-4 md:px-8 pb-3">
          {showBack && (
            <button
              onClick={onBack}
              className="border border-[#333] hover:border-[#ff4e15] transition-colors p-1.5 rounded-sm text-white shrink-0"
              aria-label="Back"
              data-testid="header-back-btn"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <div className="flex-1 h-1 bg-[#1f2024] rounded-full overflow-hidden">
            <div className="h-full bg-[#ff4e15] progress-fill" style={{ width: `${Math.max(2, progress)}%` }} />
          </div>
          <span className="text-xs text-white/70 shrink-0 font-medium">{Math.round(progress)}%</span>
        </div>
      )}

      {menuOpen && (
        <div className="absolute right-4 top-16 bg-[#17181b] border border-[#2a2b2f] rounded-md shadow-lg py-2 w-52 z-40" data-testid="header-menu">
          {/* Mobile-only language toggle inside menu */}
          <div className="sm:hidden px-4 py-2 border-b border-[#2a2b2f] flex items-center gap-2">
            <span className="text-xs text-white/50 uppercase tracking-wide">Lang</span>
            <button
              onClick={() => setLang('en')}
              className={`px-2 py-0.5 text-xs rounded-sm ${lang === 'en' ? 'bg-[#ff4e15] text-white font-bold' : 'bg-[#242528] text-white/70'}`}
              data-testid="menu-lang-en-btn"
            >EN</button>
            <button
              onClick={() => setLang('hi')}
              className={`px-2 py-0.5 text-xs rounded-sm ${lang === 'hi' ? 'bg-[#ff4e15] text-white font-bold' : 'bg-[#242528] text-white/70'}`}
              data-testid="menu-lang-hi-btn"
            >हिं</button>
          </div>

          {user ? (
            <>
              {user.is_admin && (
                <button
                  onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                  className="w-full text-left px-4 py-2 text-sm bg-[#ff4e15]/10 text-[#ff4e15] hover:bg-[#ff4e15]/20 flex items-center gap-2 font-semibold"
                  data-testid="menu-admin-btn"
                >
                  <LayoutDashboard size={14} /> {t('adminPanel')}
                </button>
              )}
              <button
                onClick={() => { setMenuOpen(false); navigate('/dashboard'); }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15] flex items-center gap-2"
                data-testid="menu-dashboard-btn"
              >
                <LayoutDashboard size={14} /> {t('dashboard')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15] flex items-center gap-2"
                data-testid="menu-logout-btn"
              >
                <LogOut size={14} /> {t('signOut')}
              </button>
              <div className="border-t border-[#2a2b2f] my-1" />
            </>
          ) : (
            <button
              onClick={() => { setMenuOpen(false); navigate('/login'); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15] flex items-center gap-2"
              data-testid="menu-signin-btn"
            >
              <LogIn size={14} /> {t('signIn')}
            </button>
          )}
          <a href="#help" className="block px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]">{t('help')}</a>
          <button
            onClick={() => { setMenuOpen(false); navigate('/about'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-about-btn"
          >
            {t('aboutUs')}
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate('/privacy-policy'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-privacy-btn"
          >
            {t('privacyPolicy')}
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate('/refund-policy'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-refund-btn"
          >
            {t('refundPolicy')}
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate('/shipping-return'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-shipping-btn"
          >
            {t('shippingReturn')}
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate('/terms'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-terms-btn"
          >
            {t('termsConditions')}
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate('/contact'); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#242528] hover:text-[#ff4e15]"
            data-testid="menu-contact-btn"
          >
            {t('contactUs')}
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
